import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { DBNoteSchema, NoteSchema } from './schemas';

type Note = z.infer<typeof NoteSchema>;

export const NoteCodecConfig: TransformerConfig<
  typeof NoteSchema,
  typeof DBNoteSchema,
  'id' | 'categoryId'
> = {
  name: 'Note',
  clientSchema: NoteSchema,
  dbSchema: DBNoteSchema,
  transformers: [
    // subject + categoryCode + code → noteId composite key, with the category code stored as
    // category; noteId is dropped on decode, its parts being fields of their own
    createTransformer<typeof NoteSchema, typeof DBNoteSchema>({
      encoder: {
        sourceFields: ['subject', 'categoryCode', 'code'] as const,
        dependencies: ['subject', 'categoryCode', 'code'] as const,
        encode: ({ subject, categoryCode, code }) => ({
          noteId: `${subject as Note['subject']}#${categoryCode as Note['categoryCode']}#${code as Note['code']}`,
          subject,
          category: categoryCode,
        }),
      },
      decoder: {
        sourceFields: ['noteId', 'category'] as const,
        decode: ({ category }) => ({
          ...(category !== undefined && { categoryCode: category }),
        }),
      },
    }),

    createRenameTransformer<typeof NoteSchema, typeof DBNoteSchema>('code', 'key'),
  ],
  derived: {
    id: note => buildRecordId('note', note),
    categoryId: note =>
      buildRecordId('notesCategory', { subject: note.subject, code: note.categoryCode }),
  },
};

export const encodeNoteToDb = createEncodeToDbFn(NoteCodecConfig);
export const decodeNoteFromDb = createDecodeFromDbFn(NoteCodecConfig);
